import { supabase } from '@/integrations/supabase/client';
import type { TaskAgenda } from '@/types/taskAgenda';
import { nextPayloadWithAutoSlot } from '@/services/taskAgenda/taskAgendaSchedule';
import { syncChamadosWithApi } from '@/utils/chamados/chamadosApiSync';

export type RunAgendaChamadosSyncOptions = {
  automaticSlotKey?: string;
};

function buildChamadosClosedDaysRange(agenda: TaskAgenda): number {
  const raw = agenda.payload?.chamadosClosedDaysRange;
  return typeof raw === 'number' && raw > 0 && raw <= 365 ? raw : 30;
}

/**
 * Sincronização de chamados (2 etapas) para uma linha da agenda; grava execuções em task_agenda_runs.
 */
export async function runAgendaChamadosSync(
  agenda: TaskAgenda,
  options?: RunAgendaChamadosSyncOptions
): Promise<void> {
  const automaticSlotKey = options?.automaticSlotKey;
  const nextPayload = () => nextPayloadWithAutoSlot(agenda, automaticSlotKey);
  const closedDaysRange = buildChamadosClosedDaysRange(agenda);

  const { data: runRow, error: insErr } = await supabase
    .from('task_agenda_runs')
    .insert({
      agenda_id: agenda.id,
      status: 'running',
      summary: null,
      error_message: null,
    })
    .select('id')
    .single();

  if (insErr || !runRow?.id) {
    const msg = insErr?.message || 'insert sem id';
    console.error('[task_agenda] Falha ao registrar execução (chamados):', insErr);
    throw new Error(`Não foi possível registrar a execução na agenda: ${msg}`);
  }

  const runId = runRow.id as string;

  try {
    const result = await syncChamadosWithApi({ closedDaysRange });
    const summary = [
      `Etapa 1 (abertos): API ${result.passo1.total}, salvos ${result.passo1.salvos}`,
      `Etapa 2 (finalizados/cancelados, ${closedDaysRange}d): API ${result.passo2.total}, salvos ${result.passo2.salvos}`,
    ].join(' · ');
    await supabase
      .from('task_agenda_runs')
      .update({
        finished_at: new Date().toISOString(),
        status: 'success',
        summary,
        error_message: null,
      })
      .eq('id', runId);
    await supabase
      .from('task_agenda')
      .update({
        last_run_at: new Date().toISOString(),
        last_run_status: 'success',
        last_run_summary: summary,
        payload: nextPayload(),
      })
      .eq('id', agenda.id);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    await supabase
      .from('task_agenda_runs')
      .update({
        finished_at: new Date().toISOString(),
        status: 'error',
        summary: null,
        error_message: msg,
      })
      .eq('id', runId);
    await supabase
      .from('task_agenda')
      .update({
        last_run_at: new Date().toISOString(),
        last_run_status: 'error',
        last_run_summary: msg,
        payload: nextPayload(),
      })
      .eq('id', agenda.id);
    throw e;
  }
}
